import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import logo from '../assets/images/logo.svg';
import { useVideoAnalytics } from '../utils/analytics.js';
import { setPageSEO } from '../utils/seo.js';
import './TestimonialSharePage.css';

const API_ORIGIN = (import.meta.env.VITE_API_BASE_URL || 'https://apiclientalio.azurewebsites.net')
  .replace(/\/api\/v1\/?$/i, '')
  .replace(/\/$/, '');

function firstValue(source, keys, fallback = '') {
  for (const key of keys) {
    const value = source?.[key];

    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      return value;
    }
  }

  return fallback;
}

function sanitizeHighlightedHtml(value) {
  if (!value) {
    return '';
  }

  return `${value}`
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/&lt;span class=&quot;marker-highlight&quot;&gt;/g, '<span class="marker-highlight">')
    .replace(/&lt;\/span&gt;/g, '</span>');
}

function formatDisplayDate(value) {
  if (!value) {
    return '';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function resolveMediaUrl(value) {
  if (!value) {
    return '';
  }

  const mediaUrl = `${value}`.trim();

  if (/^(https?:|data:|blob:)/i.test(mediaUrl)) {
    return mediaUrl;
  }

  if (mediaUrl.startsWith('//')) {
    return `https:${mediaUrl}`;
  }

  return `${API_ORIGIN}/${mediaUrl.replace(/^\/+/, '')}`;
}

function unwrapResponse(payload) {
  if (!payload) {
    return null;
  }

  if (Array.isArray(payload)) {
    return payload[0] || null;
  }

  if (typeof payload === 'string') {
    return { testimonialText: payload };
  }

  if (payload.data) {
    return unwrapResponse(payload.data);
  }

  if (payload.result) {
    return unwrapResponse(payload.result);
  }

  if (payload.testimonial) {
    return unwrapResponse(payload.testimonial);
  }

  return payload;
}

function getInitials(name) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function normalizeTestimonial(payload) {
  const testimonial = unwrapResponse(payload);

  if (!testimonial) {
    return null;
  }

  const name = firstValue(testimonial, [
    'Name',
    'name',
    'CustomerName',
    'customerName',
    'ClientName',
    'clientName',
    'ReviewerName',
    'reviewerName',
    'FullName',
    'fullName',
    'AuthorName',
    'authorName',
    'UserName',
    'userName'
  ], 'Customer');
  const company = firstValue(testimonial, [
    'Company',
    'company',
    'CompanyName',
    'companyName',
    'BusinessName',
    'businessName',
    'Organization',
    'organization',
    'BrandName',
    'brandName',
    'Designation',
    'designation',
    'Role',
    'role'
  ]);
  const text = firstValue(testimonial, [
    'TextRecorded',
    'textRecorded',
    'Text',
    'text',
    'Review',
    'review',
    'Message',
    'message',
    'Content',
    'content',
    'Testimonial',
    'testimonial',
    'TestimonialText',
    'testimonialText',
    'Description',
    'description',
    'Feedback',
    'feedback'
  ]);
  const highlightedText = firstValue(testimonial, [
    'TextRecordedHighlighted',
    'textRecordedHighlighted',
    'highlightedText',
    'HighlightedText'
  ]);
  const rating = Number(firstValue(testimonial, [
    'Ratings',
    'ratings',
    'Rating',
    'rating',
    'Stars',
    'stars',
    'StarRating',
    'starRating',
    'ReviewRating',
    'reviewRating'
  ], 0));
  const imageUrl = firstValue(testimonial, [
    'ImageLinkUploaded',
    'imageLinkUploaded',
    'ImageUrl',
    'imageUrl',
    'ProfileImageUrl',
    'profileImageUrl',
    'AvatarUrl',
    'avatarUrl',
    'PhotoUrl',
    'photoUrl',
    'LogoUrl',
    'logoUrl',
    'CompanyLogoUrl',
    'companyLogoUrl'
  ]);
  const videoUrl = firstValue(testimonial, [
    'VideoLinkRecorded',
    'videoLinkRecorded',
    'VideoUrl',
    'videoUrl',
    'VideoLink',
    'videoLink'
  ]);
  const createdOn = firstValue(testimonial, [
    'CreatedOn',
    'createdOn',
    'createdAt',
    'CreatedAt',
    'date',
    'Date'
  ]);
  const allowToShareOnSocial = firstValue(testimonial, [
    'AllowToShareOnSocial',
    'allowToShareOnSocial'
  ], false);
  const whiteLabel = firstValue(testimonial, [
    'WhiteLabel',
    'whiteLabel',
    'IsWhiteLabel',
    'isWhiteLabel',
    'WhiteLabelEnabled',
    'whiteLabelEnabled'
  ], false);

  return {
    name,
    company,
    text,
    highlightedHtml: sanitizeHighlightedHtml(highlightedText || text),
    rating: Number.isFinite(rating) ? Math.min(Math.max(Math.round(rating), 0), 5) : 0,
    ratingLabel: Number.isFinite(rating) ? rating.toFixed(1) : '',
    imageUrl: resolveMediaUrl(imageUrl),
    videoUrl: resolveMediaUrl(videoUrl),
    createdOn: formatDisplayDate(createdOn),
    allowToShareOnSocial: allowToShareOnSocial === true || `${allowToShareOnSocial}`.toLowerCase() === 'true',
    whiteLabel: whiteLabel === true || `${whiteLabel}`.toLowerCase() === 'true'
  };
}

function RatingStars({ rating }) {
  const filledStar = String.fromCharCode(9733);
  const emptyStar = String.fromCharCode(9734);

  return (
    <div className="testimonial-share-stars" aria-label={`${rating} out of 5 rating`}>
      {Array.from({ length: 5 }).map((_, index) => (
        <span key={index} className={index < rating ? 'is-filled' : ''}>
          {index < rating ? filledStar : emptyStar}
        </span>
      ))}
    </div>
  );
}

function TestimonialCard({ testimonial }) {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoAnalytics = useVideoAnalytics(`Testimonial video - ${testimonial.name}`);

  return (
    <article className="testimonial-share-card">
      <div className="testimonial-share-heart" aria-hidden="true">♥</div>

      <header className="testimonial-share-header">
        <div className="testimonial-share-avatar">
          {testimonial.imageUrl ? (
            <img src={testimonial.imageUrl} alt="" />
          ) : (
            <span>{getInitials(testimonial.name) || 'C'}</span>
          )}
        </div>

        <div>
          <h1>{testimonial.name}</h1>
          {testimonial.company && <p>{testimonial.company}</p>}
        </div>
      </header>

      {testimonial.videoUrl ? (
        <div className="testimonial-share-video-wrap">
          <video
            className="testimonial-share-video"
            controls
            playsInline
            preload="metadata"
            onPlay={(event) => {
              setIsVideoPlaying(true);
              videoAnalytics.onPlay(event);
            }}
            onTimeUpdate={videoAnalytics.onTimeUpdate}
            onPause={() => setIsVideoPlaying(false)}
            onEnded={(event) => {
              setIsVideoPlaying(false);
              videoAnalytics.onEnded(event);
            }}
          >
            <source src={testimonial.videoUrl} />
            Your browser does not support video playback.
          </video>

          {!testimonial.whiteLabel && isVideoPlaying && (
            <div className="testimonial-share-video-watermark" aria-hidden="true">
              <img src={logo} alt="" />
            </div>
          )}
        </div>
      ) : testimonial.highlightedHtml ? (
        <p
          className="testimonial-share-quote"
          dangerouslySetInnerHTML={{ __html: testimonial.highlightedHtml }}
        />
      ) : null}

      <p className="testimonial-share-byline">Reviews by {testimonial.name}</p>

      <div className="testimonial-share-rating-row">
        <RatingStars rating={testimonial.rating} />
        {testimonial.ratingLabel && <strong>{testimonial.ratingLabel}</strong>}
      </div>
    </article>
  );
}

function TestimonialSharePage() {
  const { testimonialId } = useParams();
  const [testimonial, setTestimonial] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  const endpoint = useMemo(() => {
    return `${API_ORIGIN}/api/v1/Testimonial/UniqueCollection/${encodeURIComponent(testimonialId || '')}`;
  }, [testimonialId]);

  useEffect(() => {
    const title = testimonial
      ? `${testimonial.name}'s Customer Testimonial | Clientalio`
      : 'Customer Testimonial | Clientalio';
    const description = testimonial?.text
      ? `${testimonial.text}`.replace(/\s+/g, ' ').trim().slice(0, 155)
      : 'Read or watch an authentic customer testimonial shared through Clientalio.';
    const canonical = `https://clientalio.com/testimonial/${encodeURIComponent(testimonialId || '')}`;

    return setPageSEO({
      title,
      description,
      canonical,
      ogTitle: title,
      ogDescription: description,
      ogImage: testimonial?.imageUrl,
      twitterTitle: title,
      twitterDescription: description,
      twitterImage: testimonial?.imageUrl
    });
  }, [testimonial, testimonialId]);

  useEffect(() => {
    const controller = new AbortController();

    async function fetchTestimonial() {
      try {
        setStatus('loading');
        setError('');

        const response = await fetch(endpoint, {
          method: 'GET',
          headers: {
            Accept: 'text/plain, application/json'
          },
          signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`Unable to load testimonial (${response.status})`);
        }

        const responseText = await response.text();
        let payload = responseText;

        try {
          payload = JSON.parse(responseText);
        } catch {
          payload = responseText;
        }

        const normalized = normalizeTestimonial(payload);

        if (!normalized) {
          throw new Error('Testimonial not found');
        }

        setTestimonial(normalized);
        setStatus('ready');
      } catch (fetchError) {
        if (fetchError.name === 'AbortError') {
          return;
        }

        setError(fetchError.message || 'Unable to load testimonial');
        setStatus('error');
      }
    }

    fetchTestimonial();

    return () => {
      controller.abort();
    };
  }, [endpoint]);

  return (
    <main className="testimonial-share-page">
      {status === 'loading' && (
        <section className="testimonial-share-state">
          <div className="testimonial-share-loader" />
          <p>Loading testimonial...</p>
        </section>
      )}

      {status === 'error' && (
        <section className="testimonial-share-state">
          <h1>Testimonial unavailable</h1>
          <p>{error}</p>
        </section>
      )}

      {status === 'ready' && testimonial && <TestimonialCard testimonial={testimonial} />}
    </main>
  );
}

export default TestimonialSharePage;
